/* Medication Manager — schedule, adherence ring, take/snooze/skip, refill alerts. */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pill, Plus, PencilSimple, Trash, Bell, PlusCircle, X, Clock } from "@phosphor-icons/react";
import health from "./healthApi";
import AdherenceRing from "./AdherenceRing";

const emptyMed = () => ({
  name: "",
  dose: "",
  doses: [{ time: "08:00", amount: 1 }],
  days_of_week: [0, 1, 2, 3, 4, 5, 6],
  active: true,
  stock_remaining: 30,
  refill_alert_days: 5,
  pharmacy: "",
  color: "#48CAE4",
});

// Convert any medication (legacy or new) into a normalised { doses: [{time, amount}] } shape
const normaliseDoses = (m) => {
  if (Array.isArray(m.doses) && m.doses.length > 0) return m.doses;
  return (m.schedule_times || []).map((t) => ({ time: t, amount: 1 }));
};

const fmtAmount = (n) => {
  const v = Number(n) || 0;
  if (v === 0.5) return "½";
  if (v === 1.5) return "1½";
  if (Number.isInteger(v)) return String(v);
  return v.toString();
};

export default function MedicationManager() {
  const [meds, setMeds] = useState([]);
  const [adh, setAdh] = useState({ adherence_pct: 0, taken: 0, expected: 0 });
  const [editing, setEditing] = useState(null);
  const [todayLogs, setTodayLogs] = useState([]);

  const load = async () => {
    const [m, a, logs] = await Promise.all([
      health.get("/meds").then((r) => r.data),
      health.get("/meds/adherence?days=1").then((r) => r.data),   // TODAY only
      health.get("/meds/logs?days=1").then((r) => r.data),
    ]);
    setMeds(m); setAdh(a); setTodayLogs(logs);
  };
  useEffect(() => { load(); }, []);

  const isTakenToday = (medId, time) => {
    const today = new Date().toISOString().slice(0, 10);
    return todayLogs.some(
      (l) => l.medication_id === medId && l.action === "taken" &&
             l.logged_at.startsWith(today) && (!time || l.scheduled_time === time)
    );
  };

  const doAction = async (med, time, amount, action) => {
    try {
      await health.post("/meds/log", { medication_id: med.id, action, scheduled_time: time, amount });
      toast.success(action === "taken" ? `${med.name} · ${fmtAmount(amount)} tablet(s) logged` : "Snoozed 15m");
      load();
    } catch (e) {
      toast.error("Failed to log");
    }
  };

  const save = async () => {
    try {
      // Normalise: drop empty dose rows, clamp amounts
      const cleaned = {
        ...editing,
        doses: (editing.doses || []).filter((d) => d.time).map((d) => ({
          time: d.time,
          amount: Math.max(0.25, Number(d.amount) || 1),
        })),
      };
      await health.post("/meds", cleaned);
      toast.success("Medication saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this medication?")) return;
    await health.delete(`/meds/${id}`);
    toast.success("Deleted");
    load();
  };

  // Total tablets consumed per day = sum of dose amounts
  const dailyTabletsFor = (m) => normaliseDoses(m).reduce((s, d) => s + Number(d.amount || 0), 0);
  const lowStock = meds.filter((m) => {
    const perDay = dailyTabletsFor(m) || 1;
    return m.active && (m.stock_remaining / perDay) <= m.refill_alert_days;
  });

  return (
    <div data-testid="nl-medication-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="font-label text-[10px] tracking-widest" style={{ color: "var(--nl-accent)" }}>SMART MEDICATION</div>
          <div className="font-display text-2xl font-black" style={{ color: "var(--nl-text)" }}>Today's schedule</div>
        </div>
        <AdherenceRing percent={adh.adherence_pct} label={`TODAY · ${adh.taken}/${adh.expected}`} />
      </div>

      {lowStock.length > 0 && (
        <div
          data-testid="nl-refill-alert"
          className="mb-4 rounded-lg px-4 py-3 text-sm flex items-center gap-2"
          style={{ background: "rgba(245,158,11,0.12)", border: "1px solid var(--nl-warn)", color: "var(--nl-warn)" }}
        >
          <Bell size={16} weight="fill" />
          <b>Refill needed:</b>{" "}
          {lowStock.map((m) => `${m.name} (${Math.floor(m.stock_remaining / m.daily_doses)}d left)`).join(" · ")}
          {lowStock[0]?.pharmacy && <span> — Call {lowStock[0].pharmacy}</span>}
        </div>
      )}

      <div className="space-y-3">
        {meds.length === 0 && (
          <div className="rounded-lg p-6 text-center text-sm" style={{ background: "var(--nl-panel)", color: "var(--nl-muted)" }}>
            No medications yet. Tap <b style={{ color: "var(--nl-accent)" }}>+ Add medication</b> below to start.
          </div>
        )}
        {meds.map((m) => (
          <div key={m.id} className="rounded-lg p-4" style={{ background: "var(--nl-panel)", border: "1px solid var(--nl-line)" }}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center"
                     style={{ background: m.color + "22", border: `1px solid ${m.color}` }}>
                  <Pill size={18} weight="fill" color={m.color} />
                </div>
                <div>
                  <div className="font-display font-bold" style={{ color: "var(--nl-text)" }}>{m.name}</div>
                  <div className="text-xs" style={{ color: "var(--nl-muted)" }}>
                    {m.dose} · {m.stock_remaining} left
                  </div>
                </div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`nl-med-edit-${m.id}`} onClick={() => setEditing(m)}
                        className="p-1.5 rounded" style={{ color: "var(--nl-muted)" }}>
                  <PencilSimple size={16} />
                </button>
                <button onClick={() => del(m.id)} className="p-1.5 rounded" style={{ color: "var(--nl-danger)" }}>
                  <Trash size={16} />
                </button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {normaliseDoses(m).map((d) => {
                const done = isTakenToday(m.id, d.time);
                return (
                  <div key={d.time} className="flex items-center gap-2 rounded px-2 py-1"
                       style={{ background: done ? "rgba(16,185,129,0.15)" : "var(--nl-bg-2)",
                                border: `1px solid ${done ? "var(--nl-ok)" : "var(--nl-line)"}` }}>
                    <Clock size={12} color={done ? "var(--nl-ok)" : "var(--nl-muted)"} />
                    <span className="text-xs font-mono" style={{ color: done ? "var(--nl-ok)" : "var(--nl-text)" }}>{d.time}</span>
                    <span className="text-[10px] font-label tracking-widest"
                          style={{ color: "var(--nl-muted)" }} title={`${d.amount} tablet(s)`}>
                      · {fmtAmount(d.amount)} TAB
                    </span>
                    {!done ? (
                      <>
                        <button
                          data-testid={`nl-med-take-${m.id}-${d.time}`}
                          onClick={() => doAction(m, d.time, d.amount, "taken")}
                          className="text-[10px] font-label tracking-widest px-2 py-0.5 rounded"
                          style={{ background: "var(--nl-accent)", color: "#001018" }}
                        >
                          TAKE
                        </button>
                        <button
                          onClick={() => doAction(m, d.time, d.amount, "snoozed")}
                          className="text-[10px] font-label tracking-widest px-2 py-0.5 rounded"
                          style={{ border: "1px solid var(--nl-line)", color: "var(--nl-muted)" }}
                        >
                          15M
                        </button>
                      </>
                    ) : (
                      <span className="text-[10px] font-label tracking-widest" style={{ color: "var(--nl-ok)" }}>✓ TAKEN</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <button
        data-testid="nl-med-add-btn"
        onClick={() => setEditing(emptyMed())}
        className="mt-4 w-full h-11 rounded-lg font-label text-xs tracking-widest flex items-center justify-center gap-2"
        style={{ border: "1px dashed var(--nl-accent)", color: "var(--nl-accent)" }}
      >
        <Plus size={14} weight="bold" /> ADD MEDICATION
      </button>

      {editing && (
        <MedEditor med={editing} onChange={setEditing} onSave={save} onCancel={() => setEditing(null)} />
      )}
    </div>
  );
}

function MedEditor({ med, onChange, onSave, onCancel }) {
  const doses = Array.isArray(med.doses) && med.doses.length ? med.doses : normaliseDoses(med);

  const updateDose = (i, key, val) => {
    const next = [...doses];
    next[i] = { ...next[i], [key]: key === "amount" ? Number(val) : val };
    onChange({ ...med, doses: next });
  };
  const addDose = () => onChange({ ...med, doses: [...doses, { time: "12:00", amount: 1 }] });
  const removeDose = (i) => onChange({ ...med, doses: doses.filter((_, x) => x !== i) });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur p-2 sm:p-4 overflow-y-auto">
      <div className="w-full max-w-lg rounded-t-2xl sm:rounded-2xl p-6 my-4"
           style={{ background: "var(--nl-bg-2)", border: "1px solid var(--nl-line)" }}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl font-black" style={{ color: "var(--nl-text)" }}>
            {med.id ? "Edit medication" : "New medication"}
          </div>
          <button onClick={onCancel} style={{ color: "var(--nl-muted)" }}><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <Field label="Name">
            <input value={med.name} onChange={(e) => onChange({ ...med, name: e.target.value })}
                   className="nl-input" placeholder="e.g. Lamotrigine" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Strength / description">
              <input value={med.dose} onChange={(e) => onChange({ ...med, dose: e.target.value })} className="nl-input" placeholder="200mg" />
            </Field>
            <Field label="Stock (tablets remaining)">
              <input type="number" step="0.5" value={med.stock_remaining}
                     onChange={(e) => onChange({ ...med, stock_remaining: Number(e.target.value) })}
                     className="nl-input" />
            </Field>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="font-label text-[10px] tracking-widest" style={{ color: "var(--nl-muted)" }}>
                DOSES · UP TO 8 · EACH ROW = ONE TIME + AMOUNT
              </div>
              <button
                type="button"
                onClick={addDose}
                disabled={doses.length >= 8}
                data-testid="nl-med-add-dose"
                className="text-[10px] font-label tracking-widest px-2 py-1 rounded disabled:opacity-40"
                style={{ border: "1px solid var(--nl-accent)", color: "var(--nl-accent)" }}
              >
                + ADD TIME
              </button>
            </div>
            <div className="space-y-2">
              {doses.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="time" value={d.time} onChange={(e) => updateDose(i, "time", e.target.value)}
                         className="nl-input" style={{ flex: 1 }}
                         data-testid={`nl-med-dose-time-${i}`} />
                  <select value={d.amount}
                          onChange={(e) => updateDose(i, "amount", e.target.value)}
                          className="nl-input" style={{ flex: 1 }}
                          data-testid={`nl-med-dose-amount-${i}`}>
                    <option value={0.5}>½ tab</option>
                    <option value={1}>1 tab</option>
                    <option value={1.5}>1½ tab</option>
                    <option value={2}>2 tab</option>
                    <option value={2.5}>2½ tab</option>
                    <option value={3}>3 tab</option>
                    <option value={4}>4 tab</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => removeDose(i)}
                    disabled={doses.length <= 1}
                    className="p-2 rounded disabled:opacity-30"
                    style={{ color: "var(--nl-danger)" }}
                    aria-label="Remove dose"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-2 text-[10px]" style={{ color: "var(--nl-muted)" }}>
              Daily total: <b style={{ color: "var(--nl-accent)" }}>
                {doses.reduce((s, d) => s + (Number(d.amount) || 0), 0)}
              </b> tablet(s)
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Refill alert (days)"><input type="number" value={med.refill_alert_days} onChange={(e) => onChange({ ...med, refill_alert_days: Number(e.target.value) })} className="nl-input" /></Field>
            <Field label="Pharmacy"><input value={med.pharmacy} onChange={(e) => onChange({ ...med, pharmacy: e.target.value })} className="nl-input" placeholder="Dischem 011..." /></Field>
          </div>
          <Field label="Colour">
            <div className="flex gap-2">
              {["#48CAE4", "#F59E0B", "#10B981", "#EF4444", "#8B5CF6", "#EC4899"].map((c) => (
                <button key={c} type="button" onClick={() => onChange({ ...med, color: c })}
                        className="h-8 w-8 rounded-full transition-transform"
                        style={{ background: c, transform: med.color === c ? "scale(1.15)" : "scale(1)",
                                 border: med.color === c ? "2px solid #fff" : "2px solid transparent" }} />
              ))}
            </div>
          </Field>
        </div>
        <div className="mt-6 flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 rounded font-label text-xs" style={{ border: "1px solid var(--nl-line)", color: "var(--nl-muted)" }}>CANCEL</button>
          <button data-testid="nl-med-save-btn" onClick={onSave} className="px-5 py-2 rounded font-label text-xs" style={{ background: "var(--nl-accent)", color: "#001018" }}>SAVE</button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="mb-1 font-label text-[10px] tracking-widest" style={{ color: "var(--nl-muted)" }}>{label}</div>
    {children}
  </label>
);
